import React, { useState } from 'react';
import { 
    Box, Typography, Card, CardContent, Button, Dialog, DialogTitle, 
    DialogContent, DialogActions, Chip, Alert, TextField, FormControl, 
    InputLabel, Select, MenuItem, Grid, Divider, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Paper, Checkbox, IconButton, Tooltip as MuiTooltip
} from '@mui/material';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PostAddIcon from '@mui/icons-material/PostAdd';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import DeleteIcon from '@mui/icons-material/Delete';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';

export default function UtilityView() {
    const { 
        showToast, 
        offlineOrdersQueue, 
        removeOfflineQueueItem,
        clearOfflineQueue,
        syncOfflineOrders, 
        syncTenantDataFromDatabase, 
        emergencyRecoverStuckOrders, 
        quickInsertMissingBill,
        isOnline,
        currentRole
    } = useApp();
    const { t } = useLanguage();

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [cleared, setCleared] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const [recoveryResult, setRecoveryResult] = useState(null);

    // Offline Queue Manager States
    const [selectedOfflineIds, setSelectedOfflineIds] = useState([]);
    const [confirmClearQueueOpen, setConfirmClearQueueOpen] = useState(false);
    const [isSyncingQueue, setIsSyncingQueue] = useState(false);

    // Quick Bill Entry Form State
    const [quickBill, setQuickBill] = useState({
        order_id: '',
        table_id: 'counter',
        total: '',
        payment_method: 'cash',
        type: 'Counter Order'
    });
    const [isSavingQuickBill, setIsSavingQuickBill] = useState(false);

    // Keys that must NEVER be cleared (login, tenant, auth)
    const PROTECTED_KEYS = [
        'dinexa_is_logged_in',
        'dinexa_active_tenant_subdomain',
        'dinexa_tenant_subdomain',
        'react_pos_current_user',
        'react_pos_current_role',
        'react_pos_allowed_menus',
        'react_pos_locked_actions'
    ];

    // Normalize items in offline queue for robust display
    const queueList = Array.isArray(offlineOrdersQueue) ? offlineOrdersQueue : [];

    const handleToggleSelectOffline = (id) => {
        setSelectedOfflineIds(prev => {
            const exists = prev.includes(id);
            return exists ? prev.filter(item => item !== id) : [...prev, id];
        });
    };

    const handleToggleSelectAllOffline = () => {
        if (selectedOfflineIds.length === queueList.length) {
            setSelectedOfflineIds([]);
        } else {
            setSelectedOfflineIds(queueList.map(item => item.id || item.order_id));
        }
    };

    const handleDeleteSingleOffline = (id) => {
        removeOfflineQueueItem(id);
        setSelectedOfflineIds(prev => prev.filter(item => item !== id));
    };

    const handleDeleteSelectedOffline = () => {
        if (selectedOfflineIds.length === 0) return;
        selectedOfflineIds.forEach(id => removeOfflineQueueItem(id));
        showToast(`✅ निवडलेल्या ${selectedOfflineIds.length} ऑर्डर्स Queue मधून काढल्या!`);
        setSelectedOfflineIds([]);
    };

    const handleClearAllQueue = () => {
        clearOfflineQueue();
        setSelectedOfflineIds([]);
        setConfirmClearQueueOpen(false);
    };

    const handleSyncQueueNow = async () => {
        setIsSyncingQueue(true);
        try {
            await syncOfflineOrders(false);
        } finally {
            setIsSyncingQueue(false);
        }
    };

    // 1-Click Scan & Recover Action
    const handle1ClickRecovery = async () => {
        setIsScanning(true);
        setRecoveryResult(null);

        try {
            // 1. Check Offline Queue in localStorage
            let queueCount = 0;
            const queueRes = await syncOfflineOrders(true);
            if (queueRes && queueRes.success) {
                queueCount = queueRes.count || 0;
            }

            // 2. Settle any stuck cooking/pending orders for today
            let stuckRecovered = 0;
            const recoverRes = await emergencyRecoverStuckOrders();
            if (recoverRes && recoverRes.success) {
                stuckRecovered = recoverRes.recovered_count || 0;
            }

            // 3. Force re-fetch fresh state from Database
            await syncTenantDataFromDatabase();

            const totalRecovered = queueCount + stuckRecovered;
            setRecoveryResult({
                success: true,
                queueCount,
                stuckRecovered,
                totalRecovered,
                message: totalRecovered > 0 
                    ? `✅ एकूण ${totalRecovered} बिले यशस्वीरीत्या शोधून डेटाबेसमध्ये सुरक्षित सेव्ह झाली!` 
                    : `ℹ️ सर्व बिले आधीपासूनच डेटाबेसमध्ये अप-टू-डे आहेत. कोणतीही नवीन गहाळ बिले आढळली नाहीत.`
            });

            showToast(totalRecovered > 0 
                ? `✅ ${totalRecovered} बिले रिकव्हर व सेव्ह झाली!` 
                : '✅ सर्व डेटा ऑनलाईन डेटाबेसशी सिंक आहे.'
            );
        } catch (e) {
            setRecoveryResult({
                success: false,
                message: `❌ रिकव्हरी त्रुटी: ${e.message}`
            });
            showToast(`❌ रिकव्हरी त्रुटी: ${e.message}`);
        } finally {
            setIsScanning(false);
        }
    };

    // Quick Missing Bill Insert
    const handleQuickBillSubmit = async (e) => {
        e.preventDefault();
        const totalNum = parseFloat(quickBill.total);
        if (isNaN(totalNum) || totalNum <= 0) {
            showToast('⚠️ कृपया बिलाची योग्य रक्कम टाका.');
            return;
        }

        setIsSavingQuickBill(true);
        try {
            const res = await quickInsertMissingBill({
                order_id: quickBill.order_id.trim() || undefined,
                table_id: quickBill.table_id,
                type: quickBill.table_id === 'counter' ? 'Counter Order' : `Table ${quickBill.table_id}`,
                total: totalNum,
                payment_method: quickBill.payment_method
            });

            if (res && res.success) {
                setQuickBill({
                    order_id: '',
                    table_id: 'counter',
                    total: '',
                    payment_method: 'cash',
                    type: 'Counter Order'
                });
            }
        } finally {
            setIsSavingQuickBill(false);
        }
    };

    const handleClearLocalStorage = () => {
        const allKeys = Object.keys(localStorage);
        let removedCount = 0;
        allKeys.forEach(key => {
            if (!PROTECTED_KEYS.includes(key)) {
                localStorage.removeItem(key);
                removedCount++;
            }
        });
        setCleared(true);
        setConfirmOpen(false);
        showToast(`✅ ${removedCount} cached items cleared! Login session preserved.`);
        setTimeout(() => {
            window.location.reload();
        }, 1500);
    };

    if (currentRole !== 'owner' && currentRole !== 'developer') {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Alert severity="error" sx={{ maxWidth: 500, fontWeight: 700, borderRadius: 2 }}>
                    ⚠️ {t('access-denied') || 'Access Denied: Only Restaurant Owners can access the Utility & Data Recovery module.'}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 950, mx: 'auto' }}>
            <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, color: '#0f172a' }}>
                🛠️ {t('nav-utility') || 'Utility & Data Recovery'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                टॅब्लेट / पीसीवरील ऑफलाइन बिलांचे व्यवस्थापन, निवडक डिलीट, आपत्कालीन सिंक आणि कॅश व्यवस्थापन.
            </Typography>

            {/* CARD 1: OFFLINE QUEUE MANAGER & SELECTIVE MAINTENANCE */}
            <Card sx={{ 
                borderRadius: 3, 
                boxShadow: '0 4px 12px rgba(2,132,199,0.08)', 
                border: '2px solid #bae6fd', 
                bgcolor: '#ffffff',
                mb: 3 
            }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <ReceiptIcon sx={{ fontSize: 36, color: '#0284c7' }} />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#0369a1' }}>
                                    📦 ऑफलाइन साठवलेल्या ऑर्डर्स व्यवस्थापन (Offline Queue Manager)
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    नेट नसताना किंवा टेस्टिंगसाठी सेव्ह झालेली बिले तपासा, सिलेक्ट करून डिलीट करा किंवा ऑनलाईन सिंक करा.
                                </Typography>
                            </Box>
                        </Box>
                        <Chip 
                            label={`${queueList.length} प्रलंबित ऑर्डर्स`} 
                            color={queueList.length > 0 ? "warning" : "default"} 
                            sx={{ fontWeight: 800, fontSize: '0.85rem' }} 
                        />
                    </Box>

                    {queueList.length === 0 ? (
                        <Alert severity="success" sx={{ borderRadius: 2, fontWeight: 700 }}>
                            ✅ सध्या ऑफलाइन मेमरीमध्ये कोणतीही प्रलंबित किंवा अनावश्यक ऑर्डर साठवलेली नाही (Queue is 100% clean).
                        </Alert>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        disabled={selectedOfflineIds.length === 0}
                                        onClick={handleDeleteSelectedOffline}
                                        startIcon={<DeleteIcon />}
                                        sx={{ fontWeight: 800, borderRadius: 2, textTransform: 'none' }}
                                    >
                                        निवडलेले डिलीट करा ({selectedOfflineIds.length})
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="text"
                                        color="error"
                                        onClick={() => setConfirmClearQueueOpen(true)}
                                        startIcon={<CleaningServicesIcon />}
                                        sx={{ fontWeight: 800, textTransform: 'none' }}
                                    >
                                        सर्व Queue साफ करा
                                    </Button>
                                </Box>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSyncingQueue}
                                    onClick={handleSyncQueueNow}
                                    startIcon={isSyncingQueue ? <CircularProgress size={18} color="inherit" /> : <CloudSyncIcon />}
                                    sx={{ fontWeight: 900, borderRadius: 2, textTransform: 'none', bgcolor: '#0284c7', '&:hover': { bgcolor: '#0369a1' } }}
                                >
                                    {isSyncingQueue ? 'सिंक होत आहे...' : '☁️ आता ऑनलाईन सिंक करा'}
                                </Button>
                            </Box>

                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, maxHeight: 320 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedOfflineIds.length === queueList.length && queueList.length > 0}
                                                    indeterminate={selectedOfflineIds.length > 0 && selectedOfflineIds.length < queueList.length}
                                                    onChange={handleToggleSelectAllOffline}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>ऑर्डर / KOT आयडी</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>प्रकार / टेबल</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>पदार्थ तपशील</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800 }}>रक्कम</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>तारीख/वेळ</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 800 }}>कृती</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {queueList.map((item, index) => {
                                            const itemId = item.id || item.order_id || `OFF-${index}`;
                                            const isSelected = selectedOfflineIds.includes(itemId);
                                            const itemsCount = Array.isArray(item.items) ? item.items.length : 0;
                                            const itemsSummary = Array.isArray(item.items) 
                                                ? item.items.map(i => `${i.name || i.menu_item_id || 'Dish'} x${i.quantity || 1}`).join(', ')
                                                : '-';
                                            const timeStr = item.timestamp ? new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';

                                            return (
                                                <TableRow key={itemId} hover selected={isSelected}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={isSelected}
                                                            onChange={() => handleToggleSelectOffline(itemId)}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>{index + 1}</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{itemId}</TableCell>
                                                    <TableCell><Chip label={item.type || item.table_id || 'Table'} size="small" sx={{ fontWeight: 700, fontSize: '0.75rem' }} /></TableCell>
                                                    <TableCell sx={{ maxWidth: 220, fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={itemsSummary}>
                                                        {itemsCount > 0 ? `${itemsCount} पदार्थ (${itemsSummary})` : 'No items'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#16a34a' }}>
                                                        ₹{(item.total || item.subtotal || 0).toFixed(0)}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', color: '#64748b' }}>{timeStr}</TableCell>
                                                    <TableCell align="center">
                                                        <MuiTooltip title="Queue मधून काढून टाका (Delete from Offline Queue)">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteSingleOffline(itemId)}
                                                                sx={{ width: 28, height: 28, bgcolor: '#fee2e2', '&:hover': { bgcolor: '#fca5a5' } }}
                                                            >
                                                                <DeleteIcon fontSize="small" sx={{ color: '#dc2626' }} />
                                                            </IconButton>
                                                        </MuiTooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* CARD 2: 1-CLICK EMERGENCY DATA RECOVERY */}
            <Card sx={{ 
                borderRadius: 3, 
                boxShadow: '0 4px 12px rgba(0,135,81,0.12)', 
                border: '2px solid #a7f3d0', 
                bgcolor: '#f0fdf4',
                mb: 3 
            }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <FindReplaceIcon sx={{ fontSize: 40, color: '#008751' }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#065f46' }}>
                                🔄 १-क्लिक डेटा रिकव्हरी व सिंक (1-Click Emergency Recovery)
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#047857', mt: 0.5, fontWeight: 600 }}>
                                जर नेट बंद असताना बनवलेली बिले ऑनलाईन डेटाबेसमध्ये किंवा रिपोर्टमध्ये दिसत नसतील, तर खालील बटण दाबा.
                            </Typography>
                        </Box>
                    </Box>

                    <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2, bgcolor: '#ffffff', border: '1px solid #bbf7d0' }}>
                        <strong>हे बटण काय करते:</strong> हे टॅब्लेटच्या मेमरीमधील सर्व जुन्या ऑफलाइन ऑर्डर्स शोधून डेटाबेसमध्ये UPSERT पद्धतीने सुरक्षित सेव्ह करते आणि अडकलेली बिले सेटल करून रिपोर्ट त्वरित अपडेट करते.
                    </Alert>

                    {recoveryResult && (
                        <Alert 
                            severity={recoveryResult.success ? 'success' : 'error'} 
                            sx={{ mb: 2.5, borderRadius: 2, fontWeight: 700 }}
                        >
                            {recoveryResult.message}
                            {recoveryResult.totalRecovered > 0 && (
                                <Box sx={{ mt: 1, fontSize: '0.85rem', fontWeight: 600 }}>
                                    • Queue मधून सिंक झालेली बिले: <strong>{recoveryResult.queueCount}</strong><br/>
                                    • अर्धवट अडकलेली सेटल झालेली बिले: <strong>{recoveryResult.stuckRecovered}</strong>
                                </Box>
                            )}
                        </Alert>
                    )}

                    <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={isScanning}
                        onClick={handle1ClickRecovery}
                        startIcon={isScanning ? <CircularProgress size={24} color="inherit" /> : <SyncIcon sx={{ fontSize: 28 }} />}
                        sx={{ 
                            py: 1.6, 
                            fontWeight: 900, 
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            borderRadius: 2.5, 
                            bgcolor: '#008751', 
                            boxShadow: '0 4px 10px rgba(0,135,81,0.3)',
                            '&:hover': { bgcolor: '#006c40' } 
                        }}
                    >
                        {isScanning ? 'डेटा शोधून सेव्ह होत आहे...' : '🔄 गहाळ झालेली सर्व बिले शोधा व डेटाबेसवर सेव्ह करा (Scan & Recover)'}
                    </Button>
                </CardContent>
            </Card>

            {/* CARD 3: QUICK MISSING BILL ENTRY FORM */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0', mb: 3 }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                        <PostAddIcon sx={{ fontSize: 36, color: '#3b82f6' }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                ➕ छापील पावतीवरून थेट ५ सेकंदात बिल नोंद (Quick Bill Entry)
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.2 }}>
                                जर एखादे बिल कोणत्याही कारणाने मेमरीतून साफ झाले असेल, तर छापील पावतीवरून फक्त रक्कम व पेमेंट प्रकार टाकून थेट सेव्ह करा.
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box component="form" onSubmit={handleQuickBillSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="बिलाची रक्कम (Total Amount ₹)"
                                    type="number"
                                    fullWidth
                                    required
                                    placeholder="उदा. 150"
                                    value={quickBill.total}
                                    onChange={(e) => setQuickBill({ ...quickBill, total: e.target.value })}
                                    InputProps={{ sx: { fontWeight: 800, fontSize: '1.1rem' } }}
                                />
                            </Grid>

                            <Grid item xs={6} sm={4}>
                                <FormControl fullWidth>
                                    <InputLabel>पेमेंट पद्धत (Payment)</InputLabel>
                                    <Select
                                        value={quickBill.payment_method}
                                        label="पेमेंट पद्धत (Payment)"
                                        onChange={(e) => setQuickBill({ ...quickBill, payment_method: e.target.value })}
                                        sx={{ fontWeight: 700 }}
                                    >
                                        <MenuItem value="cash">💵 रोख (Cash)</MenuItem>
                                        <MenuItem value="upi">📱 ऑनलाईन (UPI / QR)</MenuItem>
                                        <MenuItem value="split">💳 विभागून (Split)</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={4}>
                                <TextField
                                    label="टेबल / पार्सल नं (पर्यायी)"
                                    fullWidth
                                    placeholder="उदा. 5 किंवा counter"
                                    value={quickBill.table_id}
                                    onChange={(e) => setQuickBill({ ...quickBill, table_id: e.target.value })}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    disabled={isSavingQuickBill || !quickBill.total}
                                    startIcon={isSavingQuickBill ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                    sx={{
                                        py: 1.2,
                                        fontWeight: 800,
                                        borderRadius: 2,
                                        bgcolor: '#2563eb',
                                        '&:hover': { bgcolor: '#1d4ed8' }
                                    }}
                                >
                                    {isSavingQuickBill ? 'डेटाबेसमध्ये सेव्ह होत आहे...' : '💾 डेटाबेसमध्ये थेट बिल सेव्ह करा (Save to Database)'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
            </Card>

            {/* CARD 4: CLEAR LOCAL STORAGE CACHE */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <DeleteSweepIcon sx={{ fontSize: 36, color: '#dc2626' }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                Clear Browser Cache
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                Browser मधील तात्पुरती cached फाइल्स साफ करा. (Login सेशन आणि Database डेटा सुरक्षित राहील).
                            </Typography>
                        </Box>
                    </Box>

                    {cleared ? (
                        <Button variant="contained" disabled startIcon={<CheckCircleIcon />} fullWidth sx={{ py: 1.2, fontWeight: 800, borderRadius: 2 }}>
                            Cache Cleared — Reloading...
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteSweepIcon />}
                            fullWidth
                            onClick={() => setConfirmOpen(true)}
                            sx={{ py: 1.2, fontWeight: 800, borderRadius: 2 }}
                        >
                            Clear Local Storage Cache
                        </Button>
                    )}
                </CardContent>
            </Card>

            {/* Confirmation Dialog for Clear Cache */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>⚠️ Confirm Clear Cache</DialogTitle>
                <DialogContent>
                    <Typography>
                        Browser मधील सर्व तात्पुरता cached डेटा साफ होईल. Login session सुरक्षित राहील. पुढे चालू ठेवायचे?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmOpen(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
                    <Button onClick={handleClearLocalStorage} variant="contained" color="error" sx={{ fontWeight: 800 }}>
                        Yes, Clear Cache
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog for Clear Offline Queue */}
            <Dialog open={confirmClearQueueOpen} onClose={() => setConfirmClearQueueOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 900, color: '#dc2626' }}>🧹 ऑफलाइन Queue साफ करा</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>
                        ऑफलाइन मेमरीमध्ये साठवलेल्या सर्व ({queueList.length}) ऑर्डर्स/KOTs नष्ट होतील व ऑनलाईन सिंक होणार नाहीत. पुढे चालू ठेवायचे?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Button onClick={() => setConfirmClearQueueOpen(false)} sx={{ fontWeight: 700 }}>रद्द करा (Cancel)</Button>
                    <Button onClick={handleClearAllQueue} variant="contained" color="error" sx={{ fontWeight: 800 }}>
                        होय, सर्व साफ करा
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
